import { supabase } from '@common/api/authService';
import LogoWithLink from '@common/components/Logo/LogoWithLink';
import {
    PublishedAppApiError,
    publishedAppSharingService,
} from '@common/api/publishedAppSharingService';
import { ArrowClockwise, LockKey, SignOut, WarningCircle } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import {
    PUBLISHED_APP_SHARE_HANDOFF_KEY,
    clearInviteFragment,
    exchangeInviteTokenForHandoff,
    readInviteTokenFromFragment,
    readShareHandoff,
    saveHostnameHandoff,
    type PublishedAppShareHandoff,
} from './shareHandoff';

type PageState =
    | { status: 'loading'; displayName?: string }
    | { status: 'error'; code: string; message: string; displayName?: string };

const errorCopy = (error: unknown, fallback: string) => {
    if (!(error instanceof PublishedAppApiError)) return { code: 'UNKNOWN', message: fallback };
    if (error.code === 'INVALID_SHARE_LINK') {
        return { code: error.code, message: 'This invitation has expired, was revoked, or is no longer valid.' };
    }
    if (error.code === 'INVITED_EMAIL_MISMATCH') {
        return { code: error.code, message: 'Sign in with the email address that received this invitation.' };
    }
    if (error.code === 'APP_ACCESS_DENIED') {
        return { code: error.code, message: 'Your Bustly account has not been invited to this page.' };
    }
    return { code: error.code, message: error.message || fallback };
};

const authRedirect = () => {
    const shareUrl = `${window.location.origin}/apps/share`;
    window.location.replace(`/admin/auth?redirect=${encodeURIComponent(shareUrl)}`);
};

export default function PublishedAppSharePage() {
    const [rawToken] = useState(() => {
        const token = readInviteTokenFromFragment(window.location.hash);
        if (!token && window.location.hash) clearInviteFragment(window.location, window.history);
        return token;
    });
    const [pageState, setPageState] = useState<PageState>({ status: 'loading' });

    useEffect(() => {
        let cancelled = false;

        const continueToApp = async () => {
            let handoff: PublishedAppShareHandoff | null = null;
            try {
                if (rawToken) {
                    handoff = await exchangeInviteTokenForHandoff(
                        rawToken,
                        (token) => publishedAppSharingService.createIntent(token),
                        window.sessionStorage,
                        window.location,
                        window.history,
                    );
                } else {
                    handoff = readShareHandoff(window.sessionStorage);
                    const hostname = new URLSearchParams(window.location.search).get('app')?.trim();
                    if (!handoff && hostname) {
                        handoff = saveHostnameHandoff(hostname, window.sessionStorage);
                    }
                }

                if (!handoff) {
                    throw new PublishedAppApiError(
                        'INVALID_SHARE_LINK',
                        'This invitation is invalid or has expired.',
                        410,
                    );
                }
                if (!cancelled) {
                    setPageState({ status: 'loading', displayName: handoff.displayName });
                }

                const session = await supabase.auth.getSession();
                if (!session.data.session?.access_token) {
                    authRedirect();
                    return;
                }

                const ticket = await publishedAppSharingService.issueTicket({
                    intent: handoff.intent,
                    hostname: handoff.hostname,
                });
                window.sessionStorage.removeItem(PUBLISHED_APP_SHARE_HANDOFF_KEY);
                window.location.replace(ticket.accessUrl);
            } catch (error) {
                if (cancelled) return;
                if (error instanceof PublishedAppApiError && error.code === 'LOGIN_REQUIRED') {
                    authRedirect();
                    return;
                }
                const copy = errorCopy(error, 'Unable to open this published page.');
                setPageState({
                    status: 'error',
                    code: copy.code,
                    message: copy.message,
                    displayName: handoff?.displayName,
                });
            }
        };

        void continueToApp();
        return () => { cancelled = true; };
    }, [rawToken]);

    const switchAccount = async () => {
        await supabase.auth.signOut();
        authRedirect();
    };

    return (
        <main className="flex min-h-screen items-center justify-center bg-[#F6F7FA] px-5 py-10 text-[#1A162F]">
            <section className="w-full max-w-md rounded-3xl border border-[#E7E8ED] bg-white p-8 shadow-[0_24px_80px_rgba(26,22,47,0.10)]">
                <div className="mb-8 flex items-center justify-between">
                    <LogoWithLink />
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F0F2F7] text-[#4D5570]">
                        {pageState.status === 'loading'
                            ? <LockKey size={20} weight="bold" />
                            : <WarningCircle size={20} weight="bold" />}
                    </div>
                </div>

                {pageState.status === 'loading' ? (
                    <div aria-live="polite">
                        <h1 className="text-2xl font-semibold tracking-[-0.02em]">
                            {pageState.displayName || 'Opening shared page'}
                        </h1>
                        <p className="mt-3 text-sm leading-6 text-[#70778E]">
                            Verifying your Bustly account and page access…
                        </p>
                        <div className="mt-8 h-1.5 overflow-hidden rounded-full bg-[#ECEEF4]">
                            <div className="h-full w-2/3 animate-pulse rounded-full bg-[#1A162F]" />
                        </div>
                    </div>
                ) : (
                    <div role="alert">
                        <h1 className="text-2xl font-semibold tracking-[-0.02em]">Unable to open page</h1>
                        <p className="mt-3 text-sm leading-6 text-[#70778E]">{pageState.message}</p>
                        <div className="mt-7 flex gap-3">
                            <button
                                type="button"
                                onClick={() => window.location.reload()}
                                className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-[#D9DCE5] px-4 py-3 text-sm font-medium hover:bg-[#F6F7FA]"
                            >
                                <ArrowClockwise size={16} weight="bold" /> Retry
                            </button>
                            {(pageState.code === 'INVITED_EMAIL_MISMATCH' || pageState.code === 'APP_ACCESS_DENIED') ? (
                                <button
                                    type="button"
                                    onClick={() => void switchAccount()}
                                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#1A162F] px-4 py-3 text-sm font-medium text-white hover:bg-[#2E2948]"
                                >
                                    <SignOut size={16} weight="bold" /> Switch account
                                </button>
                            ) : null}
                        </div>
                    </div>
                )}

                <p className="mt-8 border-t border-[#ECEEF4] pt-5 text-xs leading-5 text-[#9298A9]">
                    Protected by Bustly account access.
                </p>
            </section>
        </main>
    );
}
